import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@mui/material';

const ROWS = [
  { key: 'row1', dir: 'in' },
  { key: 'row2', dir: 'out' },
  { key: 'row3', dir: 'in' },
  { key: 'row4', dir: 'in' },
  { key: 'row5', dir: 'out' },
];

const STATS = [
  { key: 'statCalls', bg: '#fff1eb', color: '#FF6B35' },
  { key: 'statRate', bg: '#dcfce7', color: '#10b981' },
  { key: 'statDuration', bg: '#EBF0FF', color: '#3664D1' },
  { key: 'statPending', bg: '#fef3c7', color: '#92400e' },
];

export const FeatureCallLog = () => {
  const { t } = useTranslation('landing');

  return (
    <Box component="section" sx={{ py: { xs: '60px', md: '100px' }, background: '#fff', overflow: 'hidden' }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: '24px', md: '40px' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '35% 62%' }, gap: '3%', alignItems: 'center' }}>
          {/* Text — LEFT */}
          <Box className="omni-reveal">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#FF6B35', mb: 2 }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
              {t('featureCallLog.label')}
            </Box>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: 'clamp(1.6rem, 3vw, 2.2rem)' }, fontWeight: 800, lineHeight: 1.3, mb: 2, letterSpacing: '-0.3px', color: '#0f1729' }}>
              {t('featureCallLog.title')}
            </Typography>
            <Typography sx={{ fontSize: 19, color: '#374151', lineHeight: 1.8, mb: '28px' }}>
              {t('featureCallLog.desc')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {['bullet1', 'bullet2', 'bullet3'].map((key) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: '#FF6B35', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '2px', fontSize: 15, fontWeight: 700, color: '#fff' }}>&#10003;</Box>
                  <Typography sx={{ fontSize: 18, color: '#374151', lineHeight: 1.5 }}>{t(`featureCallLog.${key}`)}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              component={Link}
              to="/voice-agent"
              variant="contained"
              sx={{
                mt: 3,
                px: 4,
                py: 1.2,
                fontSize: 16,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #FF6B35, #e85d2c)',
                boxShadow: '0 4px 16px rgba(255,107,53,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e85d2c, #d44f20)',
                  boxShadow: '0 6px 20px rgba(255,107,53,0.4)',
                },
              }}
            >
              {t('featureCallLog.cta', '聽聽 AI 語音客服 →')}
            </Button>
          </Box>

          {/* Mockup — RIGHT */}
          <Box className="omni-feature-visual omni-reveal omni-reveal-delay-1">
            <Box className="omni-mockup-frame">
              <Box className="omni-mockup-chrome">
                <Box className="omni-mockup-dots"><span /><span /><span /></Box>
                <Box className="omni-mockup-title">{t('featureCallLog.mockup.title')}</Box>
              </Box>
              <Box className="mock-call">
                {/* Stat cards */}
                <Box className="mock-call-stats">
                  {STATS.map((stat) => (
                    <Box key={stat.key} className="mock-call-stat" sx={{ background: stat.bg }}>
                      <Box className="s-val" sx={{ color: stat.color }}>
                        {t(`featureCallLog.mockup.${stat.key}.value`)}
                      </Box>
                      <Box className="s-label">{t(`featureCallLog.mockup.${stat.key}.label`)}</Box>
                    </Box>
                  ))}
                </Box>

                {/* Table */}
                <table className="mock-call-table">
                  <thead>
                    <tr>
                      <th>{t('featureCallLog.mockup.headerDir')}</th>
                      <th>{t('featureCallLog.mockup.headerCaller')}</th>
                      <th>{t('featureCallLog.mockup.headerIntent')}</th>
                      <th>{t('featureCallLog.mockup.headerDuration')}</th>
                      <th>{t('featureCallLog.mockup.headerTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ROWS.map((row) => {
                      const isIncoming = row.dir === 'in';
                      return (
                        <tr key={row.key}>
                          <td>
                            <Box
                              className="mock-call-dir"
                              sx={{
                                background: isIncoming ? '#dcfce7' : '#dbeafe',
                                color: isIncoming ? '#166534' : '#1e40af',
                              }}
                            >
                              {isIncoming ? '↙' : '↗'}{' '}
                              {t(`featureCallLog.mockup.${isIncoming ? 'incoming' : 'outgoing'}`)}
                            </Box>
                          </td>
                          <td>
                            <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#0f1729' }}>
                              {t(`featureCallLog.mockup.${row.key}.name`)}
                            </Typography>
                          </td>
                          <td>
                            <Box className="mock-call-intent">
                              {t(`featureCallLog.mockup.${row.key}.intent`)}
                            </Box>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 16, color: '#374151' }}>
                              {t(`featureCallLog.mockup.${row.key}.dur`)}
                            </Typography>
                          </td>
                          <td>
                            <Typography sx={{ fontSize: 16, color: '#6b7280' }}>
                              {t(`featureCallLog.mockup.${row.key}.time`)}
                            </Typography>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
